import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Search, Send, FileCheck2, ExternalLink } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { ContactDialog, JustifyDialog } from "@/components/attendance/AbsenceDialogs";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const FILTERS = [["todo", "Da segnalare"], ["done", "Già segnalate"], ["all", "Tutte"]];
const CH = { whatsapp: "WhatsApp", email: "email", manuale: "a mano" };

function ReportedCell({ r, onToggle }) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox checked={!!r.reported_at} onCheckedChange={(v) => onToggle(r, v)} data-testid={`reported-toggle-${r.record_id}`} />
      {r.reported_at ? (
        <span className="text-xs text-emerald-700" data-testid={`reported-info-${r.record_id}`}>
          Segnalata {fmtDate(r.reported_at)} via {CH[r.reported_channel] || "—"}{r.reported_by_name ? ` · ${r.reported_by_name}` : ""}
        </span>
      ) : <span className="text-xs text-amber-700">Da segnalare</span>}
    </div>
  );
}

function AbsenceRow({ r, onContact, onJustify, onToggle }) {
  return (
    <tr className="border-t border-stone-100 hover:bg-stone-50/60" data-testid={`absence-row-${r.record_id}`}>
      <td className="p-3">
        <Link to={`/scuola/studenti/${r.student_id}`} className="font-medium text-stone-900 hover:underline">{r.student_name}</Link>
        <div className="text-xs text-stone-500">{r.matricola || ""}{r.year_level ? ` · ${r.year_level}° anno` : ""}</div>
      </td>
      <td className="p-3">
        <div className="text-sm">{fmtDate(r.date)}{r.start_time ? ` · ${r.start_time}–${r.end_time}` : ""}</div>
        {r.justified_from && (
          <div className="text-xs text-stone-600" data-testid={`absence-partial-${r.record_id}`}>
            Giustificata {r.justified_from}–{r.justified_to} · da controllare {r.unjustified_minutes} min
          </div>
        )}
        <Link to={`/scuola/presenze/${r.session_id}`} className="text-xs text-stone-500 hover:underline inline-flex items-center gap-1">
          {r.course_name} <ExternalLink className="w-3 h-3" />
        </Link>
      </td>
      <td className="p-3"><ReportedCell r={r} onToggle={onToggle} /></td>
      <td className="p-3">
        <div className="flex gap-2 justify-end flex-wrap">
          <Button size="sm" variant="outline" onClick={() => onContact(r)} data-testid={`absence-contact-btn-${r.record_id}`}><Send className="w-4 h-4 mr-1" />Segnala</Button>
          <Button size="sm" onClick={() => onJustify(r)} data-testid={`absence-justify-btn-${r.record_id}`}><FileCheck2 className="w-4 h-4 mr-1" />Giustifica</Button>
        </div>
      </td>
    </tr>
  );
}

export default function AssenzeDaGiustificare() {
  const qc = useQueryClient();
  const [since, setSince] = useState(daysAgo(30));
  const [until, setUntil] = useState(iso(new Date()));
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("todo");
  const [contact, setContact] = useState(null);
  const [justify, setJustify] = useState(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["unjustified", since, until],
    queryFn: async () => (await api.get("/attendance/unjustified", { params: { since: since || undefined, until: until || undefined } })).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["unjustified"] });
  const reportMut = useMutation({
    mutationFn: ({ id, channel }) => api.post(`/attendance/records/${id}/reported`, { channel }),
    onSuccess: refresh, onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const justifyMut = useMutation({
    mutationFn: ({ id, reason, times }) => api.post(`/attendance/records/${id}/justify`, { reason, ...times }),
    onSuccess: () => { toast.success("Assenza giustificata"); setJustify(null); refresh(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const shown = useMemo(() => {
    const n = q.trim().toLowerCase();
    return rows.filter((r) => (filter === "all" || (filter === "done") === !!r.reported_at)
      && (!n || `${r.student_name} ${r.matricola || ""} ${r.course_name || ""}`.toLowerCase().includes(n)));
  }, [rows, q, filter]);
  const todo = rows.filter((r) => !r.reported_at).length;
  const students = new Set(rows.map((r) => r.student_id)).size;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="unjustified-page">
      <PageHeader title="Assenze da giustificare" subtitle="Assenze non giustificate degli studenti residenziali: segnalale e giustificale da qui." />
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[["Assenze", rows.length, "count-total"], ["Da segnalare", todo, "count-todo"], ["Studenti", students, "count-students"]].map(([l, v, t]) => (
          <Card key={t} className="p-4"><div className="text-xs text-stone-500">{l}</div><div className="text-2xl font-semibold" data-testid={t}>{v}</div></Card>
        ))}
      </div>
      <Card className="p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-stone-400" />
          <Input className="pl-9" placeholder="Cerca studente, matricola o corso" value={q} onChange={(e) => setQ(e.target.value)} data-testid="unjustified-search" />
        </div>
        <label className="text-xs text-stone-500">Dal<Input type="date" value={since} onChange={(e) => setSince(e.target.value)} data-testid="unjustified-since" /></label>
        <label className="text-xs text-stone-500">Al<Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="unjustified-until" /></label>
        <div className="flex gap-1">
          {FILTERS.map(([k, l]) => (
            <Button key={k} size="sm" variant={filter === k ? "default" : "outline"} onClick={() => setFilter(k)} data-testid={`unjustified-filter-${k}`}>{l}</Button>
          ))}
        </div>
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="unjustified-table">
          <thead className="bg-stone-50 text-left text-xs text-stone-500">
            <tr><th className="p-3">Studente</th><th className="p-3">Lezione</th><th className="p-3">Segnalazione</th><th className="p-3 text-right">Azioni</th></tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <AbsenceRow key={r.record_id} r={r} onContact={setContact} onJustify={setJustify}
                onToggle={(row, v) => reportMut.mutate({ id: row.record_id, channel: v ? "manuale" : null })} />
            ))}
          </tbody>
        </table>
        {!isLoading && !shown.length && <p className="p-8 text-center text-stone-500" data-testid="unjustified-empty">Nessuna assenza da mostrare in questo periodo.</p>}
      </Card>
      <ContactDialog row={contact} onClose={() => setContact(null)}
        onReported={(channel) => { reportMut.mutate({ id: contact.record_id, channel }); toast.success("Assenza segnalata"); setContact(null); }} />
      {justify && <JustifyDialog row={justify} pending={justifyMut.isPending} onClose={() => setJustify(null)}
        onConfirm={(reason, times) => justifyMut.mutate({ id: justify.record_id, reason, times })} />}
    </div>
  );
}
